import { computed, type Ref } from 'vue'
import type { Light } from '@/stores/lights'
import { useSceneLightConfigsStore } from '@/stores/sceneLightConfigs'
import { useScenesStore } from '@/stores/scenes'
import { isLightInColorMode, type ColorModeContext } from '@/lib/colorMode'

export function useLightColorMode(currentSceneId: Ref<string | null>) {
  const sceneLightConfigsStore = useSceneLightConfigsStore()
  const scenesStore = useScenesStore()

  const currentScene = computed(() => {
    if (!currentSceneId.value) return null
    return scenesStore.scenes.get(currentSceneId.value) || null
  })

  /**
   * Create context for color mode utilities
   */
  const getContext = (): ColorModeContext => ({
    currentSceneId: currentSceneId.value,
    currentScene: currentScene.value,
    getConfig: (sceneId, lightId) => sceneLightConfigsStore.getConfig(sceneId, lightId) || undefined
  })

  /**
   * Check if a light is in color mode (hue/saturation) vs white mode (color temperature)
   */
  const isInColorMode = (light: Light): boolean => {
    return isLightInColorMode(light, getContext())
  }

  /**
   * Toggle light between color mode and white mode
   */
  const toggleColorMode = async (light: Light, toWhiteMode: boolean) => {
    if (!currentSceneId.value) return

    const existingConfig = sceneLightConfigsStore.getConfig(currentSceneId.value, light.id)

    if (toWhiteMode) {
      // Switch to white mode - REPLACE state with only white fields
      const newState = {
        on: light.current_state?.on ?? true,
        brightness: light.current_state?.bri ?? 127,
        color_temperature: 327
      }

      if (existingConfig) {
        await sceneLightConfigsStore.updateConfig(existingConfig.id, {
          target_state: newState
        })
      } else {
        await sceneLightConfigsStore.createConfig({
          scene_id: currentSceneId.value,
          light_id: light.id,
          target_state: newState,
          transition_duration: 400
        })
      }
    } else {
      // Switch to color mode - REPLACE state with only color fields
      const newState = {
        on: light.current_state?.on ?? true,
        brightness: light.current_state?.bri ?? 127,
        hue: 0,
        saturation: 254
      }

      if (existingConfig) {
        await sceneLightConfigsStore.updateConfig(existingConfig.id, {
          target_state: newState
        })
      } else {
        await sceneLightConfigsStore.createConfig({
          scene_id: currentSceneId.value,
          light_id: light.id,
          target_state: newState,
          transition_duration: 400
        })
      }
    }

    // Force a refresh to ensure the UI updates
    await sceneLightConfigsStore.fetchConfigs()
  }

  return {
    isInColorMode,
    toggleColorMode
  }
}
